"use client"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { Button, buttonVariants } from "@/components/ui/button"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"
import { Input } from "./ui/input"
import { useJoinMailList } from "@/api/demo"
import { toast } from "sonner"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

const emailSchema = z.object({
  email: z.string().email("Invalid email address"),
})
type EmailSchema = z.infer<typeof emailSchema>

export const DemoDialog = () => {
  const [open, setOpen] = useDemoDialogListener()
  const joinMailList = useJoinMailList()

  const emailForm = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = emailForm

  const onSubmit = (data: EmailSchema) => {
    if (data.email) {
      joinMailList.mutate({ email: data.email })
      toast.success("You'll hear from us soon!")
      reset()
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl">Like what you see?</DialogTitle>
          <DialogDescription className="text-base">
            Sign up for the full Genie experience
          </DialogDescription>
        </DialogHeader>
        <div className="z-20 space-y-8">
          <div className="space-y-2">
            <h3 className="font-medium">With a Cosine account, you can:</h3>
            <ul className="ml-6 list-disc space-y-1 text-sm">
              <li>Give any kind of software task to Genie</li>
              <li>Assign Genie issues from your ticket manager</li>
              <li>Make edits in-platform to Genie's work</li>
              <li>Integrate Genie directly into Slack</li>
              <li>
                Have Genie automatically create PRs, respond to comments and iterate on CI results
              </li>
              <li>Integrate Vercel to preview Genie's changes in your app instantly</li>
            </ul>
          </div>
          <Link href={"/login"} className={cn(buttonVariants({ variant: "default" }), "w-full")}>
            Sign Up
          </Link>
          <Separator />
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2 pt-2">
          <h3 className="text-sm">Not sure yet? Join our mailing list for updates:</h3>
          <div className="flex w-full gap-2">
            <div className="grow">
              <Input {...register("email")} type="email" placeholder="Email" className="w-full" />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <Button type="submit" className="w-fit">
              Join
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const OPEN_DEMO_DIALOG_EVENT = "open-demo-dialog"

export const useDemoDialogListener = () => {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener(OPEN_DEMO_DIALOG_EVENT, handler)
    return () => window.removeEventListener(OPEN_DEMO_DIALOG_EVENT, handler)
  }, [])
  return [open, setOpen] as const
}

export const openDemoDialog = () => window?.dispatchEvent(new Event(OPEN_DEMO_DIALOG_EVENT))
